import type { DiscoveredIp } from "../shared/utils/discovery";
import { http } from "../services/http";

// Mirrors GrantStatus and DelegatedIP in common/api/grant_status.go.
export interface DelegatedIP {
  IP: string;
  Status: number;
  // "delegated_config", "delegated_link" or "agentic_access"; agentic IPs show
  // in the agentic panel.
  Source?: string;
  // The agentic access name, shown in a tooltip.
  Name?: string;
  // RFC3339; absent when the grant has no explicit expiry.
  ExpiresAt?: string | null;
}

export interface ACL {
  ACLID?: string;
  URL: string;
  Description?: string;
  Category?: string;
  Icon?: string;
  Status: number;
  ClickToGrant?: boolean;
  // A trust policy wants the user to verify before this knoc grants.
  TrustChallengeRequired?: boolean;
  // The server is still checking the connection; a later poll settles it.
  TrustPending?: boolean;
  // Set when a passed trust challenge capped the grant below the knoc's duration.
  TrustGrantCapSeconds?: number;
  RequireTicketNumber?: boolean;
  RequireTOTP?: boolean;
  CGNATOpen?: boolean;
  HideFromUsers?: boolean;
  DisableForUser?: boolean;
  AutoBrowseUsers?: boolean;
  InstantReferrerRedirect?: boolean;
  GrantTypeFixedIps?: boolean;
  ExpiredAfter?: string;
  ParentID?: string;
  Error?: string;
  Config?: {
    UriToken?: string;
  };
  PropagationDelayMs?: number;
  // null is the portal default; 0 is never wait.
  MaxRedirectWaitMs?: number | null;
  PersistentAndAgenticEnabled?: boolean;
  PersistentIPs?: DelegatedIP[];
}

export interface User {
  ACLs?: ACL[];
  AllowAgenticAccess?: boolean;
  AllowPersistentIPs?: boolean;
  // Shown when the user matches no access paths.
  SamlGroups?: string[];
  // Global flag; false hides the delegated and agentic panels.
  PersistentAndAgenticEnabled?: boolean;
  // False when the session-slot pool is full, which holds the user on the session-limit screen.
  HasSessionSlot?: boolean;
  // This browser signed in to the admin portal and has not signed out.
  // ServerUpdateAvailable rides on it and carries no version.
  AdminPortalUser?: boolean;
  ServerUpdateAvailable?: boolean;
  [key: string]: any;
}

// A Knocproxy knoc's token goes on the tile's URL.
export async function getUserStatus(options: { signal?: AbortSignal } = {}): Promise<User> {
  const user = await http.get<User>("/status", options);
  if (!user.ACLs) user.ACLs = [];
  user.ACLs.forEach((acl: ACL) => {
    if (acl.Config?.UriToken) {
      const url = new URL(acl.URL);
      url.searchParams.set("knoctoken", acl.Config.UriToken);
      acl.URL = url.toString();
    }
  });
  return user;
}

export interface GrantAclInput {
  aclId: string;
  ips: DiscoveredIp[];
  ticketNumber?: string;
  totpCode?: string;
}

export function grantAcl(input: GrantAclInput): Promise<void> {
  return http.post("/grant", {
    ACLID: input.aclId,
    IPs: input.ips,
    TicketNumber: input.ticketNumber || null,
    TOTPCode: input.totpCode || null,
  });
}

export function revokeAcl(aclId: string): Promise<void> {
  return http.delete(`/sessions/current/granted-acls/${encodeURIComponent(aclId)}`);
}

export interface Theme {
  Accent: string;
  HighAccessibility: boolean;
  [field: string]: unknown;
}

// Unauthenticated, for the login screens.
export function getTheme(options: { signal?: AbortSignal } = {}): Promise<Theme> {
  return http.get<Theme>("/theme", options);
}

// Logout aborts this so it cannot land afterwards and re-grant an address.
export function submitDiscoveredIps(ips: DiscoveredIp[], options: { signal?: AbortSignal } = {}): Promise<void> {
  return http.post("/discovered-ips", { IPs: ips }, options);
}
